import type { FormEvent } from "react";
import { useContext, useEffect, useState } from "react";
import { Redirect, useHistory } from "react-router-dom";

import { log } from "@repo/helpers";
import { OneTimePasswordPage } from "@repo/ui";

import { AccountController } from "../../api/accountController";
import { AuthContext } from "./context";
import { useAuthSettings, useMfaTypes } from "./hooks";

export function TLPCloudOTPPage() {
	const { username, returnUrl, selectedMfaType, rememberMe, clearStorage } = useContext(AuthContext);
	const [otp, setOtp] = useState<string>("");
	const [disabled, setDisabled] = useState<boolean>(false);
	const [error, setError] = useState<string>("");
	const [success, setSuccess] = useState<string>("");
	const [loggingIn, setLoggingIn] = useState<boolean>(false);
	const [resendLoading, setResendLoading] = useState<boolean>(false);
	const [rememberMachine, setRememberMachine] = useState<boolean>(true);
	const history = useHistory();
	const authSettingsQuery = useAuthSettings(username);
	const typesQuery = useMfaTypes(username);

	useEffect(() => {
		document.title = "TLP Cloud - Enter Multi Factor Authentication Code";
	}, []);

	async function resendCode() {
		setResendLoading(true);
		setError("");
		setSuccess("");
		const response = await AccountController.requestMfaCode(username, selectedMfaType);
		if (!response.success) {
			setError(response.message ?? "An error occurred while resending your multi factor code");
		} else {
			setSuccess("Multi Factor Code resent");
		}
		setResendLoading(false);
	}

	async function submit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		if (!otp) return;
		setDisabled(true);
		setLoggingIn(true);
		await login();
	}

	useEffect(() => {
		if (otp.length < 6) {
			setDisabled(true);
		} else {
			setDisabled(false);
		}
	}, [otp]);

	function handleError(error: any) {
		log(error, "error");
		setError("An error occurred while logging in");
		setDisabled(false);
		setLoggingIn(false);
	}

	async function login() {
		setError("");
		setSuccess("");
		try {
			const response = await AccountController.oneTimePassword(username, otp, rememberMachine);

			if (!response.success) {
				setError(response.message ?? "Invalid username or password");
				setDisabled(false);
				setLoggingIn(false);
			} else {
				if (!rememberMe) clearStorage();
				if (returnUrl) {
					window.location.replace(returnUrl);
				}
				window.location.replace(response.returnUrl);
			}
		} catch (error) {
			handleError(error);
		}
	}

	if (!username) {
		return <Redirect to="/Login" />;
	}

	const selected = typesQuery.data?.find((t) => t.id === selectedMfaType);

	return (
		<OneTimePasswordPage
			password={otp}
			setPassword={setOtp}
			submitFunction={submit}
			resendCode={resendCode}
			resendLoading={resendLoading}
			type={selectedMfaType}
			obfuscation={selected?.obfuscation}
			rememberMe={authSettingsQuery.data?.canStoreMachine ? rememberMachine : undefined}
			setRememberMe={authSettingsQuery.data?.canStoreMachine ? setRememberMachine : undefined}
			disabled={disabled}
			loading={loggingIn}
			error={error}
			success={success}
			goBack={() => history.goBack()}
			backgroundImage={authSettingsQuery.data?.backgroundImage}
			logoImage={authSettingsQuery.data?.companyLogo}
		/>
	);
}
